import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDateTime, maskIban } from '@/utils/format';
import type { WithdrawalDraft } from '@/hooks/useWithdrawalFlow';

type RecapDisplayProps = {
  draft: WithdrawalDraft;
  currency: string;
  reference: string;
  createdAt: string;
  onApprove: () => void;
  approving: boolean;
};

export default function RecapDisplay({ draft, currency, reference, createdAt, onApprove, approving }: RecapDisplayProps) {
  const { t } = useTranslation();

  const rows = [
    { icon: 'ri-user-line', label: t('recap.beneficiary'), value: draft.fullName },
    { icon: 'ri-money-dollar-circle-line', label: t('recap.amount'), value: formatCurrency(draft.amount, currency), accent: true },
    { icon: 'ri-bank-line', label: t('recap.bank'), value: draft.bankName },
    { icon: 'ri-numbers-line', label: t('recap.iban'), value: maskIban(draft.iban) },
    { icon: 'ri-mail-line', label: t('recap.email'), value: draft.email },
    { icon: 'ri-hashtag', label: t('recap.reference'), value: reference, mono: true },
    { icon: 'ri-calendar-line', label: t('recap.date'), value: formatDateTime(createdAt) },
  ];

  return (
    <div className="space-y-6 animate-fade-rise">
      <div className="flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 px-5 py-4">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-background-50">
          <i className="ri-checkbox-circle-line text-base"></i>
        </span>
        <div>
          <p className="text-sm font-semibold text-primary-800">{t('recap.successMsg')}</p>
          <p className="mt-1 text-xs text-primary-700">{t('recap.subtitle')}</p>
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
          <i className="ri-file-list-3-line text-lg text-primary-700"></i>
          {t('recap.title')}
        </h2>

        <dl className="mt-4 divide-y divide-background-200">
          {rows.map((row) => (
            <div key={row.label} className="flex flex-wrap items-start justify-between gap-3 py-3.5">
              <dt className="flex items-center gap-2 text-sm text-foreground-600 shrink-0">
                <i className={`${row.icon} text-base text-foreground-400`}></i>
                {row.label}
              </dt>
              <dd
                className={`text-sm font-semibold ${
                  row.mono ? 'font-mono tracking-wide text-foreground-800' : ''
                } ${row.accent ? 'text-primary-700' : 'text-foreground-950'}`}
              >
                {row.value}
              </dd>
            </div>
          ))}

          <div className="flex flex-wrap items-start justify-between gap-3 py-3.5">
            <dt className="flex items-center gap-2 text-sm text-foreground-600 shrink-0">
              <i className="ri-loader-2-line text-base text-foreground-400"></i>
              {t('recap.status')}
            </dt>
            <dd className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span>
              {t('recap.statusValue')}
            </dd>
          </div>
        </dl>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={onApprove}
          disabled={approving}
          className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {approving ? (
            <i className="ri-loader-4-line animate-spin text-lg"></i>
          ) : (
            <i className="ri-check-double-line text-lg"></i>
          )}
          {approving ? t('recap.approving') : t('recap.approve')}
        </button>
        <a
          href="/withdrawal"
          className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
        >
          <i className="ri-edit-line text-lg"></i>
          {t('recap.edit')}
        </a>
      </div>
    </div>
  );
}